import React, { useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react'
import { FiPlus, FiTag, FiZap } from 'react-icons/fi'
import { FileItem } from '@/types'
import { useTagManagement } from '@/hooks'
import SmartTagSuggestionsModal from '@/components/Modals/SmartTagSuggestionsModal'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface TagManagerProps {
  file: FileItem
  tags: string[]
  onTagAdded: (tagName: string) => void
  onTagRemoved: (tagName: string) => void
}

export default function TagManager({ file, tags, onTagAdded, onTagRemoved }: TagManagerProps) {
  const [newTagName, setNewTagName] = useState('')
  const { isAdding, isRemoving, addTag, removeTag } = useTagManagement()
  const {
    isOpen: isSmartTagModalOpen,
    onOpen: onSmartTagModalOpen,
    onClose: onSmartTagModalClose,
  } = useDisclosure()
  const { t } = useAppTranslation('files')

  const handleAddTag = async () => {
    if (!newTagName.trim()) return

    const tagName = newTagName.trim()

    // Check if tag already exists
    if (tags.includes(tagName)) {
      return
    }

    const success = await addTag(file.id, tagName)
    if (success) {
      onTagAdded(tagName)
      setNewTagName('')
    }
  }

  const handleRemoveTag = async (tagName: string) => {
    const success = await removeTag(file.id, tagName)
    if (success) {
      onTagRemoved(tagName)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isAdding) {
      handleAddTag()
    }
  }

  const handleSmartTagsAdded = (addedTags: string[]) => {
    addedTags.forEach((tag) => onTagAdded(tag))
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Current Tags */}
      <Box>
        <HStack spacing={2} mb={3} justify="space-between">
          <HStack spacing={2}>
            <FiTag />
            <Text fontWeight="semibold">{t('tagManager.currentTags')}</Text>
            <Badge variant="subtle" colorScheme="blue">
              {tags.length}
            </Badge>
          </HStack>
          <Button
            size="sm"
            colorScheme="purple"
            variant="outline"
            leftIcon={<FiZap />}
            onClick={onSmartTagModalOpen}
            isDisabled={Boolean(isAdding || isRemoving)}
          >
            {t('tagManager.smartTags')}
          </Button>
        </HStack>

        {tags.length > 0 ? (
          <Wrap spacing={2}>
            {tags.map((tag) => (
              <WrapItem key={tag}>
                <Tag
                  size="md"
                  variant="solid"
                  colorScheme="blue"
                  opacity={isRemoving === tag ? 0.5 : 1}
                >
                  <TagLabel>{tag}</TagLabel>
                  <TagCloseButton
                    onClick={() => handleRemoveTag(tag)}
                    isDisabled={isRemoving === tag}
                  />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        ) : (
          <Alert status="info" variant="subtle">
            <AlertIcon />
            {t('tagManager.noTagsMessage')}
          </Alert>
        )}
      </Box>

      {/* Add New Tag */}
      <Box>
        <Text fontWeight="semibold" mb={3}>
          {t('tagManager.addNewTag')}
        </Text>
        <HStack spacing={2}>
          <Input
            placeholder={t('tagManager.enterTagName')}
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={handleKeyPress}
            isDisabled={isAdding}
          />
          <Button
            leftIcon={isAdding ? <Spinner size="xs" /> : <FiPlus />}
            onClick={handleAddTag}
            isDisabled={!newTagName.trim() || isAdding}
            colorScheme="green"
          >
            {t('tagManager.add')}
          </Button>
        </HStack>
      </Box>

      {/* Smart Tag Suggestions Modal */}
      <SmartTagSuggestionsModal
        isOpen={isSmartTagModalOpen}
        onClose={onSmartTagModalClose}
        file={file}
        existingTags={tags}
        onTagsAdded={handleSmartTagsAdded}
      />
    </VStack>
  )
}
